import { useReducer, useCallback, useMemo } from "react";
import { FiltersConfig } from "../../types";

type FilterState = Record<string, string[]>;

interface FiltersState {
  draft: FilterState;
  committed: FilterState;
}

type FilterAction =
  | { type: "TOGGLE_FILTER_OPTION"; filterTitle: string; optionValue: string }
  | { type: "RESET_FILTERS" }
  | { type: "SET_FILTER"; filterTitle: string; values: string[] }
  | { type: "CLEAR_FILTER"; filterTitle: string }
  | { type: "RESTORE_STATE"; state: FilterState }
  | { type: "COMMIT_DRAFT" }
  | { type: "RESET_TO_COMMITTED" };

// Map of filter titles to their URL parameter names
const filterTitleToParamMap: Record<string, string> = {
  "By Status": "status",
  "By Document Type": "type",
  "By Issue Date": "issue_date",
  "By Customer": "customer",
  "By Due Date": "due_on",
};

const filterReducer = (
  state: FiltersState,
  action: FilterAction
): FiltersState => {
  switch (action.type) {
    case "TOGGLE_FILTER_OPTION": {
      const { filterTitle, optionValue } = action;
      const currentValues = state.draft[filterTitle] || [];
      const isSelected = currentValues.includes(optionValue);

      return {
        ...state,
        draft: {
          ...state.draft,
          [filterTitle]: isSelected
            ? currentValues.filter((value) => value !== optionValue)
            : [...currentValues, optionValue],
        },
      };
    }

    case "RESET_FILTERS":
      return {
        draft: {},
        committed: {},
      };

    case "SET_FILTER": {
      const { filterTitle, values } = action;
      return {
        ...state,
        draft: {
          ...state.draft,
          [filterTitle]: values,
        },
      };
    }

    case "CLEAR_FILTER": {
      const { filterTitle } = action;
      const newDraft = { ...state.draft };
      delete newDraft[filterTitle];
      return {
        ...state,
        draft: newDraft,
      };
    }

    case "RESTORE_STATE":
      return {
        ...state,
        draft: action.state,
      };

    case "COMMIT_DRAFT":
      return {
        ...state,
        committed: { ...state.draft },
      };

    case "RESET_TO_COMMITTED":
      return {
        ...state,
        draft: { ...state.committed },
      };

    default:
      return state;
  }
};

interface UseFiltersReturn {
  filterState: FilterState;
  toggleFilterOption: (filterTitle: string, optionValue: string) => void;
  isOptionSelected: (filterTitle: string, optionValue: string) => boolean;
  resetFilters: () => void;
  setFilter: (filterTitle: string, values: string[]) => void;
  clearFilter: (filterTitle: string) => void;
  hasCommitedFilters: boolean;
  activeFiltersCount: number;
  getActiveFilters: () => Array<{ title: string; values: string[] }>;
  fields: FiltersConfig;
  commitDraft: () => void;
  resetToCommitted: () => void;
  getKeyValuePair: () => Record<string, string>;
}

export const useFilters = (fields: FiltersConfig): UseFiltersReturn => {
  const [state, dispatch] = useReducer(filterReducer, {
    draft: {},
    committed: {},
  });

  const toggleFilterOption = useCallback(
    (filterTitle: string, optionValue: string) => {
      dispatch({ type: "TOGGLE_FILTER_OPTION", filterTitle, optionValue });
    },
    []
  );

  const isOptionSelected = useCallback(
    (filterTitle: string, optionValue: string) =>
      (state.draft[filterTitle] || []).includes(optionValue),
    [state.draft]
  );

  const resetFilters = () => dispatch({ type: "RESET_FILTERS" });

  const setFilter = (filterTitle: string, values: string[]) =>
    dispatch({ type: "SET_FILTER", filterTitle, values });

  const clearFilter = (filterTitle: string) =>
    dispatch({ type: "CLEAR_FILTER", filterTitle });

  const commitDraft = () => dispatch({ type: "COMMIT_DRAFT" });

  const resetToCommitted = () => dispatch({ type: "RESET_TO_COMMITTED" });

  const hasCommitedFilters = useMemo(
    () =>
      Object.keys(state.committed).some(
        (key) => state.committed[key].length > 0
      ),
    [state.committed]
  );

  const activeFiltersCount = useMemo(
    () =>
      Object.values(state.draft).reduce(
        (total, values) => total + values.length,
        0
      ),
    [state.draft]
  );

  const getActiveFilters = useCallback(
    () =>
      Object.entries(state.draft)
        .filter(([, values]) => values.length > 0)
        .map(([title, values]) => ({ title, values })),
    [state.draft]
  );

  const getKeyValuePair = useCallback(() => {
    const keyValuePairs: Record<string, string> = {};
    Object.entries(state.draft).forEach(([filterTitle, values]) => {
      if (values.length > 0) {
        const paramName = filterTitleToParamMap[filterTitle];
        keyValuePairs[paramName] = values.join(",");
      }
    });

    return keyValuePairs;
  }, [state.draft]);

  return {
    toggleFilterOption,
    commitDraft,
    getActiveFilters,
    resetToCommitted,
    getKeyValuePair,
    isOptionSelected,
    resetFilters,
    setFilter,
    clearFilter,
    filterState: state.draft,
    hasCommitedFilters,
    activeFiltersCount,
    fields,
  };
};
